import { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import {
  FiChevronDown,
  FiCheckCircle,
  FiCreditCard,
  FiMapPin,
  FiSliders,
  FiX,
} from 'react-icons/fi';
import { apiUrl } from '../../config/api';
import { PROPERTY_TYPE_GROUPS } from '../../data/propertyTypes';

const FILTER_LABELS = {
  q: 'Búsqueda',
  type: 'Tipo', location: 'Ubicación', minPrice: 'Precio desde', maxPrice: 'Precio hasta',
  rooms: 'Ambientes', bedrooms:'Dormitorios', bathrooms: 'Baños', minSurface: 'Superficie desde', maxSurface:'Superficie hasta', garage: 'Garage',
  isAptoCredito: 'Apto crédito', condition: 'Estado', currency: 'Moneda',
  province: 'Provincia', department: 'Departamento', neighborhood: 'Barrio', feature:'Característica',
  petsAllowed: 'Acepta mascotas', pool: 'Pileta', quincho: 'Quincho',
  garden: 'Jardín', terrace: 'Terraza', balcony: 'Balcón',
  privateNeighborhood: 'Barrio privado',
  guests: 'Huéspedes', checkIn: 'Llegada', checkOut: 'Salida',
};

const formatValue = (key, value) => {
  if (key === 'minPrice' || key === 'maxPrice') return new Intl.NumberFormat('es-AR').format(value);
  if (key === 'minSurface') return `${value} m²`;
  if (key === 'guests') return `${value} ${Number(value) === 1 ? 'huésped' : 'huéspedes'}`;
  if (['checkIn', 'checkOut'].includes(key)) return new Date(`${value}T12:00:00`).toLocaleDateString('es-AR', { day: 'numeric', month: 'short' });
  if (value === true) return FILTER_LABELS[key];
  return String(value).replace(/_/g, ' ');
};

function ChoiceChips({ label, name, value, options, onChange, className }) {
  return <ChoiceField className={className}>
    <FieldLabel>{label}</FieldLabel>
    <ChoiceRow>
      {options.map(([optionValue, optionLabel]) => <ChoiceButton
        type="button"
        key={optionValue || 'any'}
        $active={String(value || '') === String(optionValue)}
        onClick={() => onChange(name, optionValue || undefined)}
      >{optionLabel}</ChoiceButton>)}
    </ChoiceRow>
  </ChoiceField>;
}

export default function PropertyFilters({
  filters = {}, search = '', onSearchChange, onChange, onLocationSelect, hideOperationTabs = false,
  eyebrow = 'ENCONTRÁ TU LUGAR', title = 'Afiná tu búsqueda', vacationMode = false,
}) {
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [locationInput, setLocationInput] = useState(filters.location || '');
  const [suggestions, setSuggestions] = useState([]);
  const [activeSuggestion, setActiveSuggestion] = useState(0);

  useEffect(() => setLocationInput(filters.location || ''), [filters.location]);

  useEffect(() => {
    const cleanValue = locationInput.trim();
    const currentValue = String(filters.location || '').trim();
    if (cleanValue === currentValue || (cleanValue.length > 0 && cleanValue.length < 2)) return undefined;

    const timer = window.setTimeout(() => {
      onChange({ target: { name: 'location', value: cleanValue || undefined } });
    }, 550);

    return () => window.clearTimeout(timer);
  }, [locationInput, filters.location, onChange]);

  useEffect(() => {
    const query = locationInput.trim();
    if (query.length < 2 || query === filters.location) {
      setSuggestions([]);
      return undefined;
    }

    const timer = window.setTimeout(async () => {
      try {
        const response = await fetch(apiUrl(`/locations/autocomplete?q=${encodeURIComponent(query)}`));
        if (!response.ok) throw new Error('No se pudieron cargar ubicaciones');
        const data = await response.json();
        setSuggestions(Array.isArray(data) ? data.slice(0, 6) : []);
        setActiveSuggestion(0);
      } catch {
        setSuggestions([]);
      }
    }, 280);

    return () => window.clearTimeout(timer);
  }, [locationInput, filters.location]);

  const update = (name, value) => {
    onChange({ target: { name, value: value === '' ? undefined : value } });
  };

  const handleField = (event) => {
    const { name, value, type, checked } = event.target;
    update(name, type === 'checkbox' ? checked : value);
  };

  const applyLocation = (value = locationInput, item = null) => {
    const cleanValue = String(value).trim();
    setLocationInput(cleanValue);
    if (item && onLocationSelect) onLocationSelect(item);
    else update('location', cleanValue || undefined);
    setSuggestions([]);
    setActiveSuggestion(0);
  };

  const handleLocationKeyDown = (event) => {
    if (event.key === 'ArrowDown' && suggestions.length) {
      event.preventDefault();
      setActiveSuggestion((index) => Math.min(index + 1, suggestions.length - 1));
      return;
    }
    if (event.key === 'ArrowUp' && suggestions.length) {
      event.preventDefault();
      setActiveSuggestion((index) => Math.max(index - 1, 0));
      return;
    }
    if (event.key === 'Escape') {
      setSuggestions([]);
      return;
    }
    if (event.key === 'Enter') {
      event.preventDefault();
      const selected = suggestions[activeSuggestion];
      applyLocation(selected ? (selected.name || selected.value || locationInput) : locationInput, selected);
    }
  };

  const reset = () => {
    Object.keys(filters).forEach((key) => {
      if (!(hideOperationTabs && key === 'operation')) update(key, undefined);
    });
    onSearchChange?.(undefined);
    setLocationInput('');
    setSuggestions([]);
  };

  const activeFilters = useMemo(() => {
    const entries = Object.entries(filters).filter(([key, value]) => (
      key !== 'operation'
      && !(filters.location && ['province','department','neighborhood'].includes(key))
      && value !== undefined && value !== '' && value !== false
    ));
    if (search?.trim()) entries.unshift(['q', search.trim()]);
    return entries;
  }, [filters, search]);

  return (
    <FilterSection aria-label="Filtros de propiedades">
      <FilterShell>
        <FilterIntro>
          <div>
            <Eyebrow>{eyebrow}</Eyebrow>
            <FilterTitle>{title}</FilterTitle>
          </div>
          <FilterActions>
            <AutoStatus><FiCheckCircle /> Resultados automáticos</AutoStatus>
            <ResetButton type="button" onClick={reset} disabled={!activeFilters.length && (!filters.operation || hideOperationTabs)}>
              Limpiar filtros
            </ResetButton>
          </FilterActions>
        </FilterIntro>

        {!hideOperationTabs && <OperationTabs aria-label="Tipo de operación">
          {[
            ['', 'Todas'], ['venta', 'Comprar'], ['alquiler', 'Alquiler'], ['alquiler_temporal', 'Temporal'],
          ].map(([value, label]) => (
            <OperationButton
              type="button"
              key={value || 'all'}
              $active={(filters.operation || '') === value}
              onClick={() => update('operation', value || undefined)}
            >
              {label}
            </OperationButton>
          ))}
        </OperationTabs>}

        <PrimaryFields>
          <Field>
            <FieldLabel>Tipo de propiedad</FieldLabel>
            <SelectWrap>
              <Select name="type" value={filters.type || ''} onChange={handleField}>
                <option value="">Todas las propiedades</option>
                {PROPERTY_TYPE_GROUPS.map((group) => (
                  <optgroup key={group.label} label={group.label}>
                    {group.options.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                  </optgroup>
                ))}
              </Select>
              <FiChevronDown />
            </SelectWrap>
          </Field>

          <Field $wide>
            <FieldLabel>Zona</FieldLabel>
            <LocationField>
              <FiMapPin aria-hidden="true" />
              <TextInput
                value={locationInput}
                placeholder="Barrio, ciudad, calle o provincia"
                onChange={(event) => setLocationInput(event.target.value)}
                onBlur={() => window.setTimeout(() => setSuggestions([]), 150)}
                onKeyDown={handleLocationKeyDown}
                aria-autocomplete="list"
                aria-expanded={suggestions.length > 0}
              />
              {locationInput && (
                <ClearLocation type="button" aria-label="Quitar ubicación" onClick={() => {
                  setLocationInput('');
                  if (onLocationSelect) onLocationSelect(null);
                  else applyLocation('');
                }}>
                  <FiX />
                </ClearLocation>
              )}
              {suggestions.length > 0 && (
                <Suggestions>
                  {suggestions.map((item, index) => {
                    const value = item.name || item.value || '';
                    return (
                      <Suggestion type="button" key={`${item.type}:${item._id || value}:${item.province || ''}`} $active={index === activeSuggestion} onMouseDown={() => applyLocation(value, item)}>
                        <FiMapPin /><span>{String(value).replace(/_/g, ' ')}<small>{item.type === 'province' ? 'Provincia' : item.type === 'department' ? `Ciudad o departamento${item.province ? ` · ${String(item.province).replace(/_/g,' ')}` : ''}` : item.type === 'address' ? `Calle o dirección${item.department ? ` · ${item.department}` : ''}` : `Barrio o zona${item.department ? ` · ${item.department}` : ''}`}</small></span>
                      </Suggestion>
                    );
                  })}
                </Suggestions>
              )}
            </LocationField>
          </Field>

          <Field>
            <FieldLabel>Precio</FieldLabel>
            <PriceFields>
              <NumberInput name="minPrice" type="number" min="0" inputMode="numeric" value={filters.minPrice || ''} placeholder="Desde" onChange={handleField} />
              <span>—</span>
              <NumberInput name="maxPrice" type="number" min="0" inputMode="numeric" value={filters.maxPrice || ''} placeholder="Hasta" onChange={handleField} />
            </PriceFields>
          </Field>

          <AdvancedButton type="button" $open={advancedOpen} onClick={() => setAdvancedOpen((value) => !value)}>
            <FiSliders /> Más filtros <FiChevronDown />
          </AdvancedButton>
        </PrimaryFields>

        {advancedOpen && (
          <AdvancedFields>
            <AdvancedGroup>
              <GroupTitle>Espacios</GroupTitle>
              <SpaceControls>
                {!vacationMode && <ChoiceChips label="Ambientes mínimos" name="rooms" value={filters.rooms} onChange={update} options={[['', 'Cualquiera'], ['1', '1+'], ['2', '2+'], ['3', '3+'], ['4', '4+'], ['5', '5+']]} />}
                <ChoiceChips label="Dormitorios mínimos" name="bedrooms" value={filters.bedrooms} onChange={update} options={[['', 'Cualquiera'], ['1', '1+'], ['2', '2+'], ['3', '3+'], ['4', '4+'], ['5', '5+']]} />
                <ChoiceChips label="Baños mínimos" name="bathrooms" value={filters.bathrooms} onChange={update} options={[['', 'Cualquiera'], ['1', '1+'], ['2', '2+'], ['3', '3+'], ['4', '4+']]} />
                {!vacationMode && <Field>
                  <FieldLabel>Superficie total</FieldLabel>
                  <SurfaceFields>
                    <SurfaceInput><NumberInput name="minSurface" type="number" min="0" value={filters.minSurface||''} placeholder="Desde" onChange={handleField}/><span>m²</span></SurfaceInput>
                    <span>—</span>
                    <SurfaceInput><NumberInput name="maxSurface" type="number" min="0" value={filters.maxSurface||''} placeholder="Hasta" onChange={handleField}/><span>m²</span></SurfaceInput>
                  </SurfaceFields>
                </Field>}
              </SpaceControls>
            </AdvancedGroup>
            <AdvancedGroup>
              <GroupTitle>Compra</GroupTitle>
              <PurchaseControls>
                <ChoiceChips label="Moneda" name="currency" value={filters.currency} onChange={update} options={[['', 'Ambas'], ['USD', 'USD'], ['ARS', 'ARS']]} />
                <Field>
                  <FieldLabel>Estado</FieldLabel>
                  <SelectWrap><Select name="condition" value={filters.condition || ''} onChange={handleField}><option value="">Cualquiera</option><option value="nuevo">A estrenar</option><option value="excelente">Excelente</option><option value="muy_bueno">Muy bueno</option><option value="bueno">Bueno</option><option value="a_reciclar">A reciclar</option></Select><FiChevronDown /></SelectWrap>
                </Field>
                <CreditGroup><FieldLabel>Financiación</FieldLabel><CreditToggle><input type="checkbox" name="isAptoCredito" checked={Boolean(filters.isAptoCredito)} onChange={handleField} /><FiCreditCard aria-hidden="true" /><span><strong>Apto crédito</strong><small>Solo propiedades con crédito hipotecario</small></span><i aria-hidden="true" /></CreditToggle></CreditGroup>
              </PurchaseControls>
            </AdvancedGroup>
            <AmenityGroup><GroupTitle>Características</GroupTitle><AmenityChecks>
              <Check><input type="checkbox" name="garage" checked={Boolean(filters.garage)} onChange={handleField} /><i /> Garage</Check>
              <Check><input type="checkbox" name="petsAllowed" checked={Boolean(filters.petsAllowed)} onChange={handleField} /><i /> Acepta mascotas</Check>
              <Check><input type="checkbox" name="pool" checked={Boolean(filters.pool)} onChange={handleField} /><i /> Pileta</Check>
              <Check><input type="checkbox" name="quincho" checked={Boolean(filters.quincho)} onChange={handleField} /><i /> Quincho</Check>
              <Check><input type="checkbox" name="garden" checked={Boolean(filters.garden)} onChange={handleField} /><i /> Jardín</Check>
              <Check><input type="checkbox" name="terrace" checked={Boolean(filters.terrace)} onChange={handleField} /><i /> Terraza</Check>
              <Check><input type="checkbox" name="balcony" checked={Boolean(filters.balcony)} onChange={handleField} /><i /> Balcón</Check>
              <Check><input type="checkbox" name="privateNeighborhood" checked={Boolean(filters.privateNeighborhood)} onChange={handleField} /><i /> Barrio privado</Check>
            </AmenityChecks></AmenityGroup>
          </AdvancedFields>
        )}

        {activeFilters.length > 0 && (
          <ActiveFilters>
            {activeFilters.map(([key, value]) => (
              <Chip key={key}>
                <span>{FILTER_LABELS[key] ? `${FILTER_LABELS[key]}: ` : ''}{formatValue(key, value)}</span>
                <button type="button" aria-label={`Quitar ${FILTER_LABELS[key] || key}`} onClick={() => {
                  if (key === 'q') {
                    onSearchChange?.(undefined);
                  } else if (key === 'location') {
                    setLocationInput('');
                    if (onLocationSelect) onLocationSelect(null);
                    else update(key, undefined);
                  } else update(key, undefined);
                }}><FiX /></button>
              </Chip>
            ))}
          </ActiveFilters>
        )}
      </FilterShell>
    </FilterSection>
  );
}

const FilterSection = styled.section`
  padding: 30px clamp(18px, 3vw, 48px) 14px;
  background: #f7f5ef;

  @media (max-width: 780px) {
    padding: 14px 10px 10px;
  }
`;
const FilterShell = styled.div`
  max-width: 1600px;
  margin: 0 auto;
  padding: clamp(22px, 2.5vw, 34px);
  background: rgba(255,255,255,.88);
  border: 1px solid rgba(42,64,70,.12);
  border-radius: 20px;
  box-shadow: 0 18px 54px rgba(42,64,70,.07);

  @media (max-width: 780px) {
    padding: 20px 14px;
    border-radius: 18px;
  }
`;
const FilterIntro = styled.div`
  display: flex; align-items: flex-end; justify-content: space-between; gap: 20px; margin-bottom: 22px;

  @media (max-width: 780px) {
    align-items: flex-start;
    flex-direction: column;
    gap: 14px;
  }
`;
const Eyebrow = styled.div`
  margin-bottom: 5px; color: #b45f40; font-size:12px; font-weight: 750; letter-spacing: .16em;
`;
const FilterTitle = styled.h1`
  margin: 0; color: #21373c; font-size: clamp(24px, 2.2vw, 35px); font-weight: 560; letter-spacing: -.04em;
`;
const ResetButton = styled.button`
  padding: 0 0 4px; color: #526267; background: transparent; border: 0; border-bottom: 1px solid currentColor;
  font: inherit; font-size: 12px; cursor: pointer;
  &:disabled { opacity: .35; cursor: default; }
`;
const FilterActions = styled.div`
  display: flex; align-items: center; gap: 18px;
  @media (max-width: 780px) { width: 100%; justify-content: space-between; gap: 12px; }
`;
const AutoStatus = styled.span`
  display: inline-flex; align-items: center; gap: 6px; color: #5d776b; font-size:12px; white-space: nowrap;
  svg { color: #b45f40; }
`;
const OperationTabs = styled.div`
  display: flex; gap: 5px; width: fit-content; max-width: 100%; margin-bottom: 22px; padding: 5px; background: #eef1ee; border: 1px solid rgba(42,64,70,.04); border-radius: 999px;
  @media (max-width: 780px) {
    display:grid;width:100%;margin-bottom:18px;padding:0;background:transparent;border:0;border-radius:0;
    grid-template-columns:repeat(2,minmax(0,1fr));gap:9px;
  }
`;
const OperationButton = styled.button`
  flex:0 0 auto;min-height:38px; padding:0 18px; color:${({$active}) => $active ? '#fff' : '#526267'};
  background:${({$active}) => $active ? '#29474f' : 'transparent'}; border:0;border-radius:999px;
  font-size:14px;font-weight:680;white-space:nowrap;cursor:pointer;transition:color .2s ease,background .2s ease,transform .2s ease;
  &:hover{color:${({$active}) => $active ? '#fff' : '#29474f'};background:${({$active}) => $active ? '#29474f' : 'rgba(255,255,255,.7)'};}
  &:focus-visible{outline:3px solid rgba(201,111,76,.25);outline-offset:2px;}
  @media(max-width:780px){
    min-height:50px;padding:0 14px;color:${({$active}) => $active ? '#fff' : '#4f6267'};
    background:${({$active}) => $active ? '#29474f' : '#f1f3f0'};border:1px solid ${({$active}) => $active ? '#29474f' : 'rgba(42,64,70,.08)'};
    border-radius:14px;font-size:13px;font-weight:700;box-shadow:${({$active}) => $active ? '0 9px 20px rgba(41,71,79,.16)' : 'none'};
    &:hover{transform:translateY(-1px);box-shadow:${({$active}) => $active ? '0 11px 22px rgba(41,71,79,.2)' : '0 7px 16px rgba(42,64,70,.07)'};}
  }
`;
const PrimaryFields = styled.div`
  display: grid; grid-template-columns: minmax(180px,.9fr) minmax(250px,1.35fr) minmax(265px,1.15fr) auto; align-items: end; gap: 12px;
  @media (max-width: 1050px) { grid-template-columns: 1fr 1.4fr; }
  @media (max-width: 780px) { grid-template-columns: 1fr; }
`;
const AdvancedFields = styled.div`
  display:grid;grid-template-columns:minmax(0,1.3fr) minmax(360px,.9fr);align-items:start;gap:14px;
  margin-top:18px;padding:16px;background:#f5f4f0;border-top:1px solid rgba(42,64,70,.1);border-radius:16px;
  animation: reveal .25s ease both;
  @keyframes reveal { from { opacity: 0; transform: translateY(-5px); } }
  @media (max-width: 1100px) {grid-template-columns:1fr;}
  @media (max-width: 780px) {grid-template-columns:1fr;padding:16px 0 0;background:transparent;border-radius:0;}
`;
const AdvancedGroup = styled.section`
  display:grid;gap:13px;min-width:0;padding:16px;background:rgba(255,255,255,.82);border:1px solid rgba(42,64,70,.1);border-radius:13px;
`;
const GroupTitle = styled.h2`
  margin:0;color:#29474f;font-size:12px;font-weight:800;letter-spacing:.11em;text-transform:uppercase;
`;
const SpaceControls = styled.div`
  display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:13px;
  @media(max-width:600px){grid-template-columns:1fr;}
`;
const PurchaseControls = styled.div`
  display:grid;grid-template-columns:1fr 1fr;gap:13px;
  > :last-child{grid-column:1 / -1;}
  @media(max-width:600px){grid-template-columns:1fr;}
`;
const Field = styled.label`
  position: relative; display: flex; min-width: 0; flex-direction: column; gap: 8px;
`;
const ChoiceField = styled.div`
  display:grid;min-width:0;gap:8px;
`;
const ChoiceRow = styled.div`
  display:flex;flex-wrap:wrap;gap:6px;padding:5px;background:#f7f7f4;border:1px solid rgba(42,64,70,.1);border-radius:12px;
`;
const ChoiceButton = styled.button`
  min-width:36px;min-height:34px;padding:0 10px;color:${({$active})=>$active?'#fff':'#607176'};background:${({$active})=>$active?'#29474f':'transparent'};
  border:0;border-radius:8px;font:inherit;font-size:12px;font-weight:720;cursor:pointer;transition:.18s ease;
  &:hover{color:${({$active})=>$active?'#fff':'#29474f'};background:${({$active})=>$active?'#29474f':'#fff'};}
  &:focus-visible{outline:2px solid #c96f4c;outline-offset:1px;}
`;
const FieldLabel = styled.span`
  color: #52666b; font-size: 13px; font-weight: 720; letter-spacing: .015em;
`;
const controlStyles = `
  width: 100%; min-height: 50px; box-sizing: border-box; color: #263f44; background: #fbfaf7;
  border: 1px solid rgba(42,64,70,.16); border-radius: 10px; font-family: inherit; font-size: 16px;
  transition: border-color .2s ease, box-shadow .2s ease, background .2s ease;
`;
const SelectWrap = styled.div`
  position: relative; svg { position: absolute; top: 50%; right: 14px; transform: translateY(-50%); pointer-events: none; }
`;
const Select = styled.select`
  ${controlStyles} padding: 0 42px 0 15px; appearance: none; cursor: pointer;
  &:focus { background: #fff; border-color: #6f8c91; box-shadow: 0 0 0 3px rgba(64,101,108,.1); outline: none; }
`;
const LocationField = styled.div`
  position: relative; display: flex; align-items: center; ${controlStyles}
  > svg { flex: 0 0 auto; margin-left: 15px; color: #b45f40; }
  &:focus-within { background: #fff; border-color: #6f8c91; box-shadow: 0 0 0 3px rgba(64,101,108,.1); }
`;
const TextInput = styled.input`
  min-width: 0; width: 100%; height: 48px; padding: 0 10px; color: inherit; background: transparent; border: 0; font: inherit;
  &:focus { outline: none; } &::placeholder { color: #7d898b; opacity: 1; font-size: 15px; }
`;
const ClearLocation = styled.button`
  display: grid; flex: 0 0 auto; width: 34px; height: 34px; margin-right: 7px; place-items: center; color: #667579;
  background: transparent; border: 0; border-radius: 50%; cursor: pointer; &:hover { background: #eef1ee; }
`;
const Suggestions = styled.div`
  position: absolute; top: calc(100% + 9px); left: 0; right: 0; z-index: 50; overflow: hidden; padding: 6px;
  background: #fff; border: 1px solid rgba(42,64,70,.12); border-radius: 12px; box-shadow: 0 22px 54px rgba(42,64,70,.16);
`;
const Suggestion = styled.button`
  display: flex; width: 100%; min-height:48px; align-items: center; gap: 9px; padding: 11px 13px; color: #334b50; background: ${({$active})=>$active?'#f3f1eb':'transparent'};
  border: 0; border-radius: 8px; font: inherit; font-size: 15px; text-align: left; text-transform: capitalize; cursor: pointer;
  &:hover { background: #f3f1eb; } svg { flex:none;color: #b45f40; }
  >span{display:grid;gap:2px;text-transform:none}small{color:#7b898b;font-size:12px;text-transform:capitalize}
`;
const PriceFields = styled.div`
  display: grid; grid-template-columns: 1fr auto 1fr; align-items: center; gap: 7px; > span { color: #a2a9a8; }

  @media (max-width: 370px) {
    grid-template-columns: 1fr;
    > span { display: none; }
  }
`;
const SurfaceFields = styled(PriceFields)``;
const NumberInput = styled.input`
  ${controlStyles} min-width: 0; padding: 0 13px; appearance: textfield;
  &::-webkit-inner-spin-button { appearance: none; }
  &:focus { background: #fff; border-color: #6f8c91; box-shadow: 0 0 0 3px rgba(64,101,108,.1); outline: none; }
  &::placeholder { color: #7d898b; opacity: 1; font-size: 15px; }
`;
const SurfaceInput = styled.div`
  position:relative;min-width:0;
  ${NumberInput}{padding-right:38px;}
  >span{position:absolute;top:50%;right:13px;color:#839093;font-size:12px;font-weight:700;transform:translateY(-50%);pointer-events:none;}
`;
const AdvancedButton = styled.button`
  display: flex; min-height: 50px; align-items: center; justify-content: center; gap: 8px; padding: 0 18px;
  color: ${({$open}) => $open ? '#fff' : '#29474f'}; background: ${({$open}) => $open ? '#29474f' : '#eef1ee'};
  border: 0; border-radius: 10px; font-size: 14px; font-weight: 720; white-space: nowrap; cursor: pointer;
  svg:last-child { transition: transform .2s; transform: rotate(${({$open}) => $open ? '180deg' : '0'}); }
  @media(max-width:780px){min-height:54px;width:100%;}
`;
const CreditGroup = styled.div`
  display:grid;gap:8px;
`;
const CreditToggle = styled.label`
  display:grid;width:fit-content;min-width:285px;max-width:100%;grid-template-columns:26px minmax(0,1fr) 18px;align-items:center;gap:9px;min-height:58px;padding:9px 11px;color:#496268;background:#f4f7f4;border:1px solid rgba(42,64,70,.12);border-radius:12px;cursor:pointer;transition:.18s ease;
  input{position:absolute;opacity:0;}
  >svg{color:#c96f4c;font-size:18px;}
  span{display:grid;gap:2px;min-width:0;}strong{font-size:13px;font-weight:760;}small{overflow:hidden;color:#718185;font-size:11px;line-height:1.25;text-overflow:ellipsis;white-space:nowrap;}
  >i{display:grid;width:18px;height:18px;place-items:center;border:1px solid #9eaaa9;border-radius:5px;}
  >i::after{content:'✓';color:#fff;font-size:12px;font-style:normal;font-weight:850;line-height:1;opacity:0;transform:scale(.65);transition:.18s ease;}
  &:hover{background:#fff;border-color:rgba(42,64,70,.24);box-shadow:0 6px 16px rgba(42,64,70,.06);}
  &:has(input:checked){color:#29474f;background:#eaf2ef;border-color:rgba(41,71,79,.3);}
  &:has(input:checked)>i{background:#29474f;border-color:#29474f;}
  &:has(input:checked)>i::after{opacity:1;transform:scale(1);}
  @media(max-width:600px){width:100%;min-width:0;}
`;
const AmenityGroup = styled.div`
  grid-column:1 / -1;display:grid;gap:13px;padding:16px;background:rgba(255,255,255,.82);border:1px solid rgba(42,64,70,.1);border-radius:13px;
`;
const AmenityChecks = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 7px;
  @media (max-width: 780px) {
    display:flex;
  }
`;
const Check = styled.label`
  display:flex;align-items:center;gap:6px;min-height:36px;padding:0 10px;color:#526267;background:#f7f7f4;border:1px solid rgba(42,64,70,.1);border-radius:999px;font-size:12px;font-weight:650;white-space:nowrap;cursor:pointer;transition:.18s ease;
  input { position: absolute; opacity: 0; }
  i { display:grid;width:15px;height:15px;place-items:center;border:1px solid #9eaaa9;border-radius:5px; }
  i::after{content:'✓';color:#fff;font-size:10px;font-style:normal;font-weight:850;line-height:1;opacity:0;transform:scale(.65);transition:.18s ease;}
  &:hover{background:#fff;border-color:rgba(42,64,70,.22);}
  &:has(input:checked){color:#29474f;background:#edf3f0;border-color:rgba(41,71,79,.25);}
  input:checked + i { background:#29474f;border-color:#29474f; }
  input:checked + i::after{opacity:1;transform:scale(1);}
`;
const ActiveFilters = styled.div`
  display: flex; flex-wrap: wrap; gap: 8px; margin-top: 20px;
  @media(max-width:780px){margin-top:16px;gap:7px;}
`;
const Chip = styled.span`
  display: inline-flex; min-height: 34px; align-items: center; gap: 8px; padding: 0 7px 0 12px; color: #365157;
  background: #edf2f0; border-radius: 999px; font-size:12px; text-transform: capitalize;
  button { display: grid; width: 23px; height: 23px; place-items: center; color: inherit; background: rgba(42,64,70,.08); border: 0; border-radius: 50%; cursor: pointer; }
  @media(max-width:780px){min-height:38px;max-width:100%;font-size:12px;span{overflow:hidden;text-overflow:ellipsis;white-space:nowrap;}button{width:27px;height:27px;flex:0 0 auto;}}
`;
